import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Configurable via REACT_APP_MIGRATION_BANNER_TTL_MS env var. Default: 7 days.
export const MIGRATION_BANNER_TTL_MS =
    Number(process.env.REACT_APP_MIGRATION_BANNER_TTL_MS) || 7 * 24 * 60 * 60 * 1000;

export interface MigrationContextEntry {
    migratedFromId: string;
    migratedFromTitle: string;
    migrationSummary: string;
    createdAt: number;
}

type NewMigrationContextEntry = Omit<MigrationContextEntry, 'createdAt'>;

export interface MigrationContextState {
    bySessionId: Record<string, MigrationContextEntry>;
}

const STORAGE_KEY = 'progpt_migration_contexts';

const loadFromStorage = (): Record<string, MigrationContextEntry> => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Record<string, MigrationContextEntry>;
        const now = Date.now();
        // Filter out expired entries on load
        return Object.fromEntries(
            Object.entries(parsed).filter(([, e]) => now - e.createdAt < MIGRATION_BANNER_TTL_MS),
        );
    } catch {
        return {};
    }
};

const saveToStorage = (bySessionId: Record<string, MigrationContextEntry>): void => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(bySessionId));
    } catch {
        // localStorage unavailable — degrade gracefully
    }
};

const migrationContextSlice = createSlice({
    name: 'migrationContext',
    initialState: (): MigrationContextState => ({ bySessionId: loadFromStorage() }),
    reducers: {
        setMigrationContext(
            state,
            action: PayloadAction<{ sessionId: string; entry: NewMigrationContextEntry }>,
        ) {
            const entry: MigrationContextEntry = { ...action.payload.entry, createdAt: Date.now() };
            state.bySessionId[action.payload.sessionId] = entry;
            saveToStorage(state.bySessionId);
        },
        clearMigrationContext(state, action: PayloadAction<string>) {
            delete state.bySessionId[action.payload];
            saveToStorage(state.bySessionId);
        },
    },
});

export const { setMigrationContext, clearMigrationContext } = migrationContextSlice.actions;
export default migrationContextSlice.reducer;
